import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  getClubSectionGames,
  getClubSectionStandings,
  getEventLogs,
  getGame,
  getSectionGames,
  getSectionStandings,
  inputScore,
  portalErrorMessage,
  reportPortalActivity,
  saveScorecardPhoto,
  startGame
} from '../../features/portal/portalService';
import {
  clubGamePath,
  clubPortalBase,
  secretaryClubPath,
  secretaryGamePath
} from './portalPaths';
import {
  PortalEmpty,
  PortalError,
  PortalLoading,
  PortalPageHeader,
  PortalCounts
} from './portalCommon';
import SectionScoreCard from '../ui/scoreboard/sectionScorecard';
import SectionTotalsCard from '../ui/scoreboard/sectionTotalsCard';
import TotalsCard from '../ui/scoreboard/totalsCard';
import GameByStatusCardPublicBasic from '../ui/gameStatusCards/gameByStatusCardPublicBasic';
import PortalLogExport from './portalLogExport';

const tabSx = { minWidth: 0, px: 1, fontSize: { xs: 11, sm: 14 } };

const PortalGames = ({ mode }) => {
  const { eventId, venueId, sectionId } = useParams();
  const { token } = useOutletContext();
  const navigate = useNavigate();
  const isPseudoSection = sectionId === '__all__' || sectionId === '__other__';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [tab, setTab] = useState(0);
  const [logScope, setLogScope] = useState(isPseudoSection ? 'event' : 'section');
  const [eventLogs, setEventLogs] = useState(null);
  const [eventLogsLoading, setEventLogsLoading] = useState(false);
  const [eventLogsError, setEventLogsError] = useState('');
  const [eventLogsReload, setEventLogsReload] = useState(0);
  const [scorecard, setScorecard] = useState(null);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [scorecardError, setScorecardError] = useState('');
  const [scorecardReload, setScorecardReload] = useState(0);
  const [busyGameId, setBusyGameId] = useState(null);
  const [actionError, setActionError] = useState('');
  const loadSeq = useRef(0);

  const load = useCallback(
    ({ silent = false } = {}) => {
      if (!token || !eventId) return undefined;
      let cancelled = false;
      const seq = ++loadSeq.current;

      if (!silent) {
        setLoading(true);
        setError('');
      }

      const request =
        mode === 'secretary'
          ? getClubSectionGames(token, venueId, sectionId)
          : getSectionGames(token, sectionId);

      request
        .then((response) => {
          // A slower silent refresh must not replace a newer response, and a
          // newer score response must not be replaced by an older list read.
          if (!cancelled && seq === loadSeq.current) setData(response);
        })
        .catch((requestError) => {
          if (!cancelled && !silent && seq === loadSeq.current) {
            setError(portalErrorMessage(requestError));
          }
        })
        .finally(() => {
          if (!cancelled && seq === loadSeq.current) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    },
    [token, mode, venueId, sectionId, eventId]
  );

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load, retry]);

  // Officials often score on a phone while walking between rinks. Coming back
  // to the tab should show what another scorer has done since it was opened.
  useEffect(() => {
    const refresh = () => {
      if (!busyGameId) load({ silent: true });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load, busyGameId]);

  const replaceGame = useCallback((updated) => {
    if (!updated) return;
    setData((current) =>
      current
        ? {
            ...current,
            games: (current.games || []).map((game) =>
              game.id === updated.id ? updated : game
            )
          }
        : current
    );
  }, []);

  const runGameAction = async (gameId, action) => {
    setBusyGameId(gameId);
    setActionError('');
    try {
      const response = await action();
      replaceGame(response?.game);
      load({ silent: true });
      return response;
    } catch (requestError) {
      setActionError(portalErrorMessage(requestError));
      return null;
    } finally {
      setBusyGameId(null);
    }
  };

  // The manual-score dialogs show their own alert, so this one rethrows rather
  // than reporting the failure twice.
  const submitManualScore = async (gameId, payload) => {
    setBusyGameId(gameId);
    try {
      const response = await inputScore(token, gameId, payload);
      replaceGame(response?.game);
      return response;
    } finally {
      setBusyGameId(null);
    }
  };

  const savePhoto = async (gameId, image) => {
    const response = await saveScorecardPhoto(token, gameId, image);
    replaceGame(response?.game);
    return response;
  };

  const openGame = useCallback(
    (gameId) =>
      navigate(
        mode === 'secretary'
          ? secretaryGamePath(eventId, venueId, sectionId, gameId)
          : clubGamePath(eventId, venueId, sectionId, gameId)
      ),
    [navigate, mode, eventId, venueId, sectionId]
  );

  const renderGameCard = (game) => (
    <GameByStatusCardPublicBasic
      key={game.id}
      game={game}
      actions={false}
      portal={{
        event: data?.event,
        openGame: () => openGame(game.id),
        startGame: (payload) =>
          runGameAction(game.id, () => startGame(token, game.id, payload)),
        submitScore: (payload) => submitManualScore(game.id, payload),
        savePhoto: (image) => savePhoto(game.id, image),
        // List rows only know that a photo exists; fetch it when the camera
        // icon is tapped so the base64 string stays out of the list response.
        loadPhoto: () =>
          getGame(token, game.id).then((response) => response?.game?.scorecardPhoto || ''),
        onChanged: () => load({ silent: true }),
        busy: busyGameId === game.id,
        photoErrorMessage: portalErrorMessage
      }}
    />
  );

  // A tab belongs to one section, so leaving that section drops the data it
  // loaded rather than showing another section's log for a moment.
  useEffect(() => {
    setTab(0);
    setLogScope(isPseudoSection ? 'event' : 'section');
    setEventLogs(null);
    setEventLogsLoading(false);
    setEventLogsError('');
    setScorecard(null);
    setScorecardLoading(false);
    setScorecardError('');
  }, [eventId, mode, venueId, sectionId, isPseudoSection]);

  const games = data?.games || [];
  const hasSections = Boolean(data?.event?.hasSections);
  const standingsAvailable =
    Boolean(data) && (!isPseudoSection || (!hasSections && sectionId === '__all__'));

  const scopedGameIds = useMemo(
    () => new Set((data?.games || []).map((game) => game.id)),
    [data]
  );

  // Whole-event totals are independent of the section this page belongs to.
  // Loading/error state is deliberately not a dependency: setting it here must
  // not cancel the request that is about to run.
  useEffect(() => {
    if (tab !== 1 || logScope !== 'event' || !token || !eventId) return undefined;

    let cancelled = false;
    setEventLogsLoading(true);
    setEventLogsError('');

    getEventLogs(token)
      .then((response) => {
        if (!cancelled) setEventLogs(response);
      })
      .catch((requestError) => {
        if (!cancelled) setEventLogsError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setEventLogsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, logScope, token, eventId, eventLogsReload]);

  // The section log and the detailed scorecard are the same leaderboard data,
  // so one fetch serves both. Its loading/error state is excluded from the
  // dependencies.
  const needsSectionStandings =
    standingsAvailable && ((tab === 1 && logScope === 'section') || tab === 2);

  useEffect(() => {
    if (!needsSectionStandings || !token) return undefined;

    let cancelled = false;
    setScorecardLoading(true);
    setScorecardError('');

    const request =
      mode === 'secretary'
        ? getClubSectionStandings(token, venueId, sectionId)
        : getSectionStandings(token, sectionId);

    request
      .then((response) => {
        if (!cancelled) setScorecard(response);
      })
      .catch((requestError) => {
        if (!cancelled) setScorecardError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setScorecardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [needsSectionStandings, token, mode, venueId, sectionId, scorecardReload]);

  const backTo =
    mode === 'secretary'
      ? secretaryClubPath(eventId, venueId)
      : clubPortalBase(eventId, venueId);

  const wholeEventSections =
    eventLogs?.view === 'sections'
      ? eventLogs.sections || []
      : eventLogs?.view === 'event'
        ? [
            {
              sectionId: '__all__',
              sectionName: 'Whole event',
              venue: '',
              sectionTotals: eventLogs.totals || { teams: [] }
            }
          ]
        : [];

  const logScopeLabel = logScope === 'section' ? 'Section' : 'Whole event';
  const logExportSections =
    logScope === 'section'
      ? scorecard?.section
        ? [scorecard.section]
        : []
      : wholeEventSections;
  const logExportEvent = logScope === 'section' ? scorecard?.event : eventLogs?.event;

  const handleLogScopeChange = (event) => {
    const nextScope = event.target.value;
    if (nextScope === logScope) return;

    setLogScope(nextScope);
    setEventLogs(null);
    setEventLogsError('');
  };

  const handleLogExported = ({ format, pageMode, teamCount }) => {
    const isSection = logScope === 'section' && Boolean(scorecard?.section);
    const sectionName = isSection
      ? scorecard.section.sectionName || 'Section'
      : 'Whole event';
    const sectionId = isSection ? scorecard.section.sectionId || null : null;
    const pageLabel = format === 'pdf' ? ` · ${pageMode === 'a4' ? 'A4' : 'Canvas'}` : '';

    reportPortalActivity(
      token,
      {
        operation: 'log.export',
        entityId: isSection ? sectionId || eventId : eventId,
        entityName: sectionName,
        summary: `${sectionName} log · ${
          format === 'xlsx' ? 'XLSX' : 'PDF'
        }${pageLabel} · ${teamCount} team${teamCount === 1 ? '' : 's'}`,
        metadata: {
          format,
          pageMode: format === 'pdf' ? pageMode : null,
          scope: isSection ? 'section' : 'event',
          sectionId,
          sectionName: isSection ? sectionName : null,
          teamCount
        }
      },
      { eventId }
    );
  };

  const isGameClickable = (team) =>
    Boolean(team?.game && scopedGameIds.has(team.game));

  const openGameFromScorecard = (team) => {
    if (isGameClickable(team)) openGame(team.game);
  };

  return (
    <Box>
      <PortalPageHeader
        title={data?.section?.name || 'Games'}
        subtitle={data?.section ? <PortalCounts {...data.section} /> : null}
        backTo={backTo}
        backLabel="Sections"
        barAction={
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={() => setRetry((value) => value + 1)}
            disabled={loading || Boolean(busyGameId)}
            sx={{ whiteSpace: 'nowrap' }}>
            Refresh
          </Button>
        }
      />

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      {!loading && !error && data && (
        <Paper elevation={1} sx={{ mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(event, value) => setTab(value)}
            variant="fullWidth"
            aria-label="Section views">
            <Tab label="Games" sx={tabSx} />
            <Tab label="Log" sx={tabSx} />
            <Tab label="Detailed Scorecard" sx={tabSx} />
          </Tabs>
        </Paper>
      )}

      {loading && <PortalLoading />}
      {!loading && error && <PortalError message={error} onRetry={() => setRetry((n) => n + 1)} />}

      {!loading && !error && tab === 0 && data && games.length === 0 && (
        <PortalEmpty>There are no games in this section.</PortalEmpty>
      )}

      {!loading && !error && tab === 0 && data && games.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="flex-start"
          gap={2}>
          {games.map((game) => renderGameCard(game))}
        </Box>
      )}

      {tab === 1 && !loading && !error && data && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          mb={2}>
          <TextField
            select
            size="small"
            label="Show"
            value={logScope}
            onChange={handleLogScopeChange}
            disabled={isPseudoSection}
            sx={{ minWidth: 180 }}>
            {!isPseudoSection && <MenuItem value="section">This section</MenuItem>}
            <MenuItem value="event">Whole event</MenuItem>
          </TextField>
          <PortalLogExport
            event={logExportEvent}
            sections={logExportSections}
            scopeLabel={logScopeLabel}
            onExported={handleLogExported}
          />
        </Box>
      )}

      {tab === 1 && logScope === 'section' && scorecardLoading && <PortalLoading />}

      {tab === 1 && logScope === 'section' && !scorecardLoading && scorecardError && (
        <PortalError
          message={scorecardError}
          onRetry={() => setScorecardReload((value) => value + 1)}
        />
      )}

      {tab === 1 &&
        logScope === 'section' &&
        !scorecardLoading &&
        !scorecardError &&
        scorecard && (
          <Box py={2}>
            {(scorecard.section?.sectionTotals?.teams || []).length === 0 ? (
              <PortalEmpty>No section log is available yet.</PortalEmpty>
            ) : (
              <Box display="flex" justifyContent="center">
                <SectionTotalsCard
                  section={scorecard.section}
                  event={scorecard.event}
                />
              </Box>
            )}
          </Box>
        )}

      {tab === 1 && logScope === 'event' && eventLogsLoading && <PortalLoading />}

      {tab === 1 && logScope === 'event' && !eventLogsLoading && eventLogsError && (
        <PortalError
          message={eventLogsError}
          onRetry={() => setEventLogsReload((value) => value + 1)}
        />
      )}

      {tab === 1 &&
        logScope === 'event' &&
        !eventLogsLoading &&
        !eventLogsError &&
        eventLogs && (
          <Box py={2}>
            {eventLogs.view === 'sections' ? (
              wholeEventSections.length === 0 ? (
                <PortalEmpty>No event logs are available yet.</PortalEmpty>
              ) : (
                <Stack spacing={2}>
                  {wholeEventSections.map((section) => (
                    <SectionTotalsCard
                      key={section.sectionId}
                      section={section}
                      event={eventLogs.event}
                    />
                  ))}
                </Stack>
              )
            ) : (eventLogs.totals?.teams || []).length === 0 ? (
              <PortalEmpty>No event totals are available yet.</PortalEmpty>
            ) : (
              <Box display="flex" justifyContent="center">
                <TotalsCard team={{ totals: eventLogs.totals }} />
              </Box>
            )}
          </Box>
        )}

      {tab === 2 && !standingsAvailable && (
        <PortalEmpty>
          Detailed scorecards are only available for a single section. Use the Log
          tab for the whole event.
        </PortalEmpty>
      )}

      {tab === 2 && standingsAvailable && scorecardLoading && <PortalLoading />}

      {tab === 2 && standingsAvailable && !scorecardLoading && scorecardError && (
        <PortalError
          message={scorecardError}
          onRetry={() => setScorecardReload((value) => value + 1)}
        />
      )}

      {tab === 2 &&
        standingsAvailable &&
        !scorecardLoading &&
        !scorecardError &&
        scorecard && (
          <Box py={2}>
            {(scorecard.rounds || []).length === 0 ? (
              <PortalEmpty>No scorecards are available yet.</PortalEmpty>
            ) : (
              <Box display="flex" flexWrap="wrap" justifyContent="center" gap={1}>
                {(scorecard.rounds || []).map(({ round, teams }) => (
                  <SectionScoreCard
                    key={round || 'unrounded'}
                    section={scorecard.section}
                    teams={teams}
                    round={round}
                    event={scorecard.event}
                    onGameClick={openGameFromScorecard}
                    isGameClickable={isGameClickable}
                    allowPointEditing={false}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
    </Box>
  );
};

export default PortalGames;
